'use client';

import React from 'react';
import {
  cn,
  formatInput,
  getInputTagType,
  InputType,
  TAdornmentProps,
  TCommonTypes,
  TGeneralInputProps,
  TInputMethods,
  TInputValidationAndError,
  TMessageBarSeverity,
} from 'types';
import { Icon, IconType } from '../icons';
import { getDecimalValue } from './utils';
import { LoadingSpinner } from '../loading-spinner';
import { MessageBar } from '../message-bar';

type TInputFieldProps = TCommonTypes & TGeneralInputProps & TInputValidationAndError & TInputMethods & {};

export const InputField = (props: Partial<TInputFieldProps>) => {
  const {
    id,
    className,
    decimals,
    label,
    disabled,
    errorMessage,
    startAdornment,
    endAdornment,
    placeholder,
    helperText,
    isValid = undefined,
    rows,
    defaultValue = '',
    ariaLabel,
    hideValidationIcon = false,
    labelClassName = '',
    maxLength,
    isLoading = false,
    errorVariant = 'messageBar',
    inputWrapperClassName = '',
    min,
    max,
  } = props;
  const [value, setValue] = React.useState('');

  // if (defaultValue && props?.value) throw new Error("Don't use defaultValue and value props simulteanously.");

  React.useEffect(() => {
    const asString = defaultValue?.toString() ?? '';
    const { formatted } = formatInput(asString, props.type as InputType, props.decimals, props.allowNegative);
    setValue(formatted);
  }, [defaultValue]);

  React.useEffect(() => {
    if (props.value === undefined || props.value === null) return;
    const asString = props.value.toString();
    const { formatted } = formatInput(asString, props.type as InputType, props.decimals, props.allowNegative);
    setValue(formatted);
  }, [props.value]);

  const onBlur = (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    onChange(event);
    if (typeof props.onBlur === 'function') {
      props.onBlur(event);
    }
  };

  const onFocus = (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (typeof props.onFocus === 'function') {
      props.onFocus(event);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<any>) => {
    if (typeof props.onKeyDown === 'function') {
      props.onKeyDown(e);
    } else {
      if (e.which === 13) {
        onSubmit(e);
      }
    }
  };

  const onChange = (event: any) => {
    const value =
      props.type === InputType.FLOAT ? getDecimalValue(event.currentTarget.value, decimals) : event.currentTarget.value;
    const { formatted, parsed } = formatInput(
      value ?? '',
      props.type as InputType,
      props.decimals,
      props.allowNegative
    );

    setValue(formatted);
    props.onChange && props.onChange(parsed, formatted, event);
  };

  const onSubmit = (event: any) => {
    const { formatted, parsed } = formatInput(
      event.currentTarget.value ?? '',
      props.type as InputType,
      props.decimals,
      props.allowNegative
    );
    setValue(formatted);
    props.onSubmit && props.onSubmit(parsed);
  };

  const commonProps = {
    id,
    type: typeof props.type === 'string' ? props.type : getInputTagType(props.type),
    'aria-placeholder': placeholder || null,
    'aria-label': ariaLabel || null,
    value,
    disabled,
    placeholder,
    ...(rows ? { rows } : {}),
    onBlur,
    onChange,
    onFocus,
    onSubmit,
    onKeyDown,
    maxLength,
    ...(min !== undefined ? { min } : {}),
    ...(max !== undefined ? { max } : {}),
  };

  const RenderLabelText = ({ className, label }: { label: string; className?: string }) => {
    if (!label) return;

    return (
      <label
        aria-label={label}
        className={cn('text-black text-base/[175%] font-semibold w-fit', disabled && 'text-neutralPlus', className)}
        htmlFor={id}
      >
        {label}
      </label>
    );
  };

  const DynamicTag = props.type === InputType.TEXT_AREA ? 'textarea' : 'input';

  return (
    <div className={cn('flex flex-col gap-1 grow-1', inputWrapperClassName)}>
      <RenderLabelText label={label} className={labelClassName} />
      <div className="relative flex items-center">
        <InputAdornment position="start" {...startAdornment} />
        <DynamicTag
          className={cn(
            'w-full bg-white border border-neutralPlus px-4 py-3 rounded-lg',
            'enabled:hover:bg-lightGrey enabled:hover:border-black',
            'text-base/[175%] font-semibold',
            disabled && 'text-neutralPlus disabled:bg-lightGrey disabled:border-neutralGrey',
            isValid === true && 'enabled:border-komorebi',
            isValid === false && 'enabled:border-poppy',
            startAdornment && 'pl-14',
            endAdornment && 'pr-14',
            className
          )}
          {...commonProps}
        />
        <InputAdornment
          position="end"
          {...endAdornment}
          isValid={isValid}
          errorMessage={errorMessage}
          hideValidationIcon={hideValidationIcon}
        />
        {isLoading && <LoadingSpinner className="absolute right-4 top-1/2 left-1/2 transform -translate-1/2" />}
      </div>
      {helperText && <RenderLabelText className="text-sm/[170%] text-right self-end" label={helperText} />}
      {!disabled &&
        isValid === false &&
        errorMessage &&
        (errorVariant === 'text' ? (
          <p className="text-alertRed text-sm">{errorMessage}</p>
        ) : (
          <MessageBar severity={TMessageBarSeverity.ERROR} className="tw-small self-end w-fit items-center py-1">
            <span className="text-base/[175%] font-semibold">{errorMessage}</span>
          </MessageBar>
        ))}
    </div>
  );
};

const InputAdornment = ({
  position,
  content,
  className,
  isValid,
  hideValidationIcon,
  onClick,
}: TAdornmentProps & Partial<TInputValidationAndError> & { position: 'start' | 'end' }) => {
  let adornmentIcon = null;
  let adornmentText = null;

  adornmentIcon = typeof content === 'object' ? content : null;
  adornmentText = typeof content === 'string' ? content : null;

  if (content === undefined && isValid !== undefined && !hideValidationIcon) {
    adornmentIcon = {
      icon: isValid ? IconType.NewNextCheck : IconType.Close,
      fill: isValid ? 'var(--color-komorebi)' : 'var(--color-poppy)',
    };
  }

  if (!adornmentIcon && !adornmentText) return null;

  return (
    <div
      className={cn(
        'flex items-center absolute mx-4 py-3',
        position === 'start' && 'left-0',
        position === 'end' && 'right-0'
      )}
      onClick={onClick}
    >
      {adornmentText && (
        <span className={cn('text-sm/[170%] font-extrabold mt-[2px]', className)}>{adornmentText}</span>
      )}
      {adornmentIcon && (
        <Icon
          icon={adornmentIcon.icon}
          fill={adornmentIcon?.fill || 'var(--color-black)'}
          width={24}
          height={24}
          className={cn(className)}
        />
      )}
    </div>
  );
};
